import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Hammer, PaintRoller, Wrench, Zap, ChefHat, Ruler, Droplets, LayoutGrid, ShieldCheck, Clock, PhoneCall, BadgeCheck, Star, Mail, Send, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { Reveal, SectionHeading } from '../components/shared';
import { hpHero, hpServices, hpSteps, hpReasons, reviews, business } from '../mock/mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = { Hammer, PaintRoller, Wrench, Zap, ChefHat, Ruler, Droplets, LayoutGrid };

function Hero() {
  return (
    <section className="relative h-[86vh] min-h-[540px] max-h-[820px] overflow-hidden">
      <img src={hpHero.image} alt="" className="absolute inset-0 w-full h-full object-cover animate-slow-zoom" />
      <div className="absolute inset-0 bg-gradient-to-r from-brand-charcoal/85 via-brand-charcoal/50 to-transparent" />
      <div className="relative h-full max-w-7xl mx-auto px-6 flex flex-col justify-center">
        <div className="max-w-xl animate-fade-up">
          <span className="inline-block text-brand-sand text-xs font-semibold uppercase tracking-[0.3em] mb-5">{hpHero.kicker}</span>
          <h1 className="font-display text-5xl md:text-7xl font-semibold text-brand-ivory leading-[1.02] text-balance">{hpHero.title}</h1>
          <p className="mt-6 text-lg text-brand-ivory/85 leading-relaxed max-w-md">{hpHero.subtitle}</p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Button asChild className="bg-brand-clay hover:bg-brand-clayDark text-white rounded-full px-8 h-14 text-base font-medium shadow-lg">
              <a href="#services">Explore Services <ArrowRight size={18} className="ml-1" /></a>
            </Button>
            <Button asChild variant="outline" className="rounded-full px-8 h-14 text-base font-medium bg-white/10 backdrop-blur border-white/40 text-white hover:bg-white hover:text-brand-charcoal">
              <Link to="/contact">Get a Free Quote</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustBar() {
  const items = [
    { icon: BadgeCheck, t: 'Vetted, Insured Pros' },
    { icon: ShieldCheck, t: 'Honest, Upfront Pricing' },
    { icon: Clock, t: 'On-Time & Tidy' },
    { icon: PhoneCall, t: 'One Call Does It All' }
  ];
  return (
    <div className="bg-brand-charcoal">
      <div className="max-w-7xl mx-auto px-6 py-5 grid grid-cols-2 md:grid-cols-4 gap-4">
        {items.map((i) => (
          <div key={i.t} className="text-brand-ivory text-sm font-medium tracking-wide flex items-center justify-center gap-2">
            <i.icon size={16} className="text-brand-clay" /> {i.t}
          </div>
        ))}
      </div>
    </div>
  );
}

function Services() {
  const { toast } = useToast();
  const [active, setActive] = useState(null); // service object or null
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });

  const openDialog = (s) => {
    setActive(s);
    setSent(false);
    setForm({ name: '', email: '', phone: '', message: '' });
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name || 'Website visitor',
        email: form.email,
        phone: form.phone,
        message: `[${active?.title} request] ${form.message}`.trim()
      });
      setSent(true);
      toast({ title: 'Request sent!', description: `We’ll email you about ${active?.title} shortly.` });
    } catch (err) {
      toast({ title: 'Something went wrong', description: 'Please try again or email us directly.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="services" className="max-w-7xl mx-auto px-6 py-20 md:py-28 scroll-mt-24">
      <Reveal>
        <SectionHeading eyebrow="What we do" title="One team for your whole home" sub="From quick fixes to full remodels — plus our dedicated Monti Floors division." />
      </Reveal>
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {hpServices.map((s, idx) => {
          const Icon = iconMap[s.icon] || Hammer;
          const isFloors = !!s.to;
          const inner = (
            <div className={`group h-full rounded-2xl p-7 border shadow-sm hover:shadow-xl transition-all ${isFloors ? 'bg-brand-forest border-brand-forest text-brand-ivory' : 'bg-white border-brand-line'}`}>
              <span className={`w-12 h-12 rounded-xl flex items-center justify-center ${isFloors ? 'bg-white/15 text-brand-ivory' : 'bg-brand-clay/10 text-brand-clay'}`}>
                <Icon size={22} />
              </span>
              <h3 className={`mt-5 font-display text-xl font-semibold ${isFloors ? 'text-brand-ivory' : 'text-brand-charcoal'}`}>{s.title}</h3>
              <p className={`mt-2 text-[15px] leading-relaxed ${isFloors ? 'text-brand-sand/85' : 'text-brand-stone'}`}>{s.text}</p>
              <span className={`mt-4 inline-flex items-center gap-1.5 text-sm font-semibold ${isFloors ? 'text-brand-ivory' : 'text-brand-clay'}`}>
                {isFloors ? 'Visit Monti Floors' : 'Request a quote'} <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </span>
            </div>
          );
          return (
            <Reveal key={s.title} delay={(idx % 4) * 70}>
              {isFloors ? (
                <Link to={s.to} className="block h-full">{inner}</Link>
              ) : (
                <button type="button" onClick={() => openDialog(s)} className="block w-full h-full text-left">{inner}</button>
              )}
            </Reveal>
          );
        })}
      </div>

      {/* email request popup */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="sm:max-w-md bg-brand-ivory">
          {sent ? (
            <div className="text-center py-4">
              <CheckCircle2 size={52} className="mx-auto text-brand-forest" />
              <h3 className="font-display text-2xl font-semibold text-brand-charcoal mt-4">Request sent!</h3>
              <p className="text-brand-stone mt-2 text-[15px]">Thanks — we’ll email you about <span className="font-medium text-brand-charcoal">{active?.title}</span> shortly.</p>
              <Button onClick={() => setActive(null)} className="mt-6 bg-brand-clay hover:bg-brand-clayDark text-white rounded-full px-7 h-12">Close</Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <div className="w-12 h-12 rounded-xl bg-brand-clay/10 text-brand-clay flex items-center justify-center mb-1">
                  <Mail size={22} />
                </div>
                <DialogTitle className="font-display text-2xl">Request a quote — {active?.title}</DialogTitle>
                <DialogDescription>Leave your email and we’ll reach out with next steps. No obligation.</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4 mt-2">
                <div className="space-y-2">
                  <Label htmlFor="svc-email">Email address *</Label>
                  <Input id="svc-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" className="h-12" autoFocus />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="svc-name">Name</Label>
                    <Input id="svc-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="h-12" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="svc-phone">Phone</Label>
                    <Input id="svc-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(571) 555-0123" className="h-12" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="svc-msg">Project details</Label>
                  <Textarea id="svc-msg" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={`Tell us about your ${active?.title.toLowerCase()} project...`} />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-brand-clay hover:bg-brand-clayDark text-white rounded-full h-13 py-3.5 font-medium">
                  {loading ? 'Sending…' : (<>Send Request <Send size={16} className="ml-1" /></>)}
                </Button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function Process() {
  return (
    <section id="process" className="bg-brand-cream scroll-mt-24">
      <div className="max-w-7xl mx-auto px-6 py-20 md:py-28">
        <Reveal><SectionHeading center eyebrow="How it works" title="Simple from start to finish" sub="Getting help around the house shouldn’t be complicated." /></Reveal>
        <div className="mt-16 grid gap-8 md:grid-cols-4">
          {hpSteps.map((s, idx) => (
            <Reveal key={s.id} delay={idx * 90}>
              <div className="relative text-center px-2">
                <div className="mx-auto w-16 h-16 rounded-2xl bg-white shadow-sm border border-brand-line flex items-center justify-center font-display text-2xl font-semibold text-brand-clay">
                  {s.id}
                </div>
                <h3 className="mt-5 font-display text-xl font-semibold text-brand-charcoal">{s.title}</h3>
                <p className="mt-2 text-brand-stone text-[15px] leading-relaxed">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Why() {
  return (
    <section id="why" className="max-w-7xl mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-2 gap-14 items-center scroll-mt-24">
      <Reveal>
        <img src="https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80" alt="Finishing a home project" className="rounded-2xl w-full h-[480px] object-cover shadow-lg" />
      </Reveal>
      <Reveal delay={120}>
        <span className="text-brand-clay text-xs font-semibold uppercase tracking-[0.25em]">Why Monti Home Pros</span>
        <h2 className="font-display text-3xl md:text-[42px] font-semibold text-brand-charcoal mt-3 leading-tight text-balance">Your neighbors for every project</h2>
        <div className="mt-8 space-y-6">
          {hpReasons.map((r) => (
            <div key={r.title} className="flex gap-4">
              <span className="w-11 h-11 rounded-xl bg-brand-clay/10 text-brand-clay flex items-center justify-center shrink-0"><BadgeCheck size={20} /></span>
              <div>
                <h3 className="font-semibold text-brand-charcoal text-lg">{r.title}</h3>
                <p className="text-brand-stone text-[15px] mt-1 leading-relaxed">{r.text}</p>
              </div>
            </div>
          ))}
        </div>
        <Button asChild className="mt-8 bg-brand-clay hover:bg-brand-clayDark text-white rounded-full px-7 h-13 py-3.5 font-medium">
          <Link to="/contact">Request a Free Quote</Link>
        </Button>
      </Reveal>
    </section>
  );
}

function CtaBand() {
  return (
    <section className="bg-brand-forest">
      <div className="max-w-7xl mx-auto px-6 py-16 md:py-20 flex flex-col md:flex-row items-center justify-between gap-8 text-center md:text-left">
        <div className="max-w-2xl">
          <span className="text-brand-sand/70 text-xs font-semibold uppercase tracking-[0.25em]">Free · No Obligation</span>
          <h2 className="font-display text-3xl md:text-[40px] font-semibold text-brand-ivory mt-3 leading-tight">Got a project? Let’s handle it.</h2>
          <p className="text-brand-sand/80 mt-3 text-[17px]">Tell us what your home needs — we’ll take it from there.</p>
        </div>
        <Button asChild className="bg-brand-clay hover:bg-brand-clayDark text-white rounded-full px-8 h-14 text-base font-medium shadow-lg shrink-0">
          <Link to="/contact">Get Your Free Quote</Link>
        </Button>
      </div>
    </section>
  );
}

function ReviewsPreview() {
  return (
    <section className="max-w-7xl mx-auto px-6 py-20 md:py-28">
      <Reveal>
        <SectionHeading center eyebrow="Customer reviews" title="Trusted by local homeowners" sub="The same care and honesty across every Monti service." />
      </Reveal>
      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {reviews.slice(0, 3).map((r, idx) => (
          <Reveal key={r.name} delay={idx * 90}>
            <div className="bg-white rounded-2xl p-7 border border-brand-line h-full shadow-sm">
              <div className="flex text-brand-clay mb-4">{[...Array(r.rating)].map((_, k) => <Star key={k} size={16} fill="currentColor" />)}</div>
              <p className="text-brand-charcoal/85 leading-relaxed text-[15px]">“{r.text}”</p>
              <p className="mt-5 font-semibold text-brand-charcoal">{r.name}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export default function HomePros() {
  return (
    <>
      <Hero />
      <TrustBar />
      <Services />
      <Process />
      <Why />
      <CtaBand />
      <ReviewsPreview />
    </>
  );
}
